import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronUp, ChevronDown, Maximize2, Minimize2, Cable as CableIcon, ArrowLeftRight, Trash2, Edit, Wifi, Router, Server, Camera, Cable, Package, Zap, HardDrive, Monitor, Printer, Laptop, Tv, Gamepad2, Music, Circle, Sliders, Eye, EyeOff, Radio, Plug } from 'lucide-react';
import { PlacedEquipment, EquipmentFace } from '../../types';
import { useShallow } from 'zustand/react/shallow';
import { useProjectStore } from '../../store/projectStore';
import { PortStage } from './PortStage';
import { makeTemplateForPorts } from '../../utils/layoutTemplates';
import { equipmentDatabase } from '../../data/equipment';
import SimpleLayoutEditor from './SimpleLayoutEditor';
import type { SimpleLayout } from '../../types';
import { buildPortEndpointId, buildPortNumberRemaps, endpointMatchesLayoutPort, formatPortLabel, getPortNamingScheme, parsePortEndpointId } from '../../utils/portNamingSchemes';
import { customLabelsForFace, getPortCustomLabel, remapPortCustomLabels, setPortCustomLabel } from '../../utils/portCustomLabels';
import { layoutPortKey } from '../../utils/portLayout';
import { endpointAtCableCapacity, maxCablesForPortKind, countCablesOnLayoutPort } from '../../utils/portConnectionLimits';
import { portChromeVisibleAtZoom } from '../../utils/canvasCameraPrefs';
import { portKindDisplayName } from '../../utils/customPortTypes';
import { subscribeRestoreFloatingEquipmentPanel } from '../../utils/equipmentPanelUi';
import { PoeModeSelect } from './Logic/PoeModeSelect';
import { isLogicExcluded, resolveDeviceModel } from '../../engine/networkLogic';
import { setNodesLogicExcluded } from '../../utils/logicParticipation';
import { useLogicStore } from '../../store/logicStore';

const PORT_TABLE_COLUMNS = [
  { key: 'name', label: 'Port Number' },
  { key: 'label', label: 'Label' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'cable', label: 'Cable' },
  { key: 'equipment', label: 'Linked Equipment' },
] as const;

type PortTableColKey = (typeof PORT_TABLE_COLUMNS)[number]['key'];

const DEFAULT_PORT_TABLE_COL_WIDTHS: Record<PortTableColKey, number> = {
  name: 96,
  label: 128,
  type: 96,
  status: 80,
  cable: 110,
  equipment: 150,
};

const MIN_PORT_TABLE_COL_WIDTH: Record<PortTableColKey, number> = {
  name: 40,
  label: 72,
  type: 40,
  status: 52,
  cable: 56,
  equipment: 64,
};

const PANEL_HEIGHT_DEFAULT = '42vh';
const PANEL_HEIGHT_MAXIMIZED = 'calc(100vh - 2.75rem)';

const PORT_TABLE_COL_WIDTHS_KEY = 'netprobuilder-port-advanced-col-widths';

function loadPortTableColWidths(): Record<PortTableColKey, number> {
  const next = { ...DEFAULT_PORT_TABLE_COL_WIDTHS };
  try {
    const raw = localStorage.getItem(PORT_TABLE_COL_WIDTHS_KEY);
    if (!raw) return next;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    PORT_TABLE_COLUMNS.forEach(({ key }) => {
      const value = parsed[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        next[key] = Math.max(MIN_PORT_TABLE_COL_WIDTH[key], Math.round(value));
      }
    });
  } catch {
    /* keep defaults */
  }
  return next;
}

function savePortTableColWidths(widths: Record<PortTableColKey, number>) {
  try {
    localStorage.setItem(PORT_TABLE_COL_WIDTHS_KEY, JSON.stringify(widths));
  } catch {
    /* ignore quota / private mode */
  }
}

// Face metadata for UI
const FACE_INFO: Record<EquipmentFace, { label: string; icon: string }> = {
  front: { label: 'Front', icon: '⬜' },
  back: { label: 'Back', icon: '⬛' },
  left: { label: 'Left', icon: '◀' },
  right: { label: 'Right', icon: '▶' },
  top: { label: 'Top', icon: '⬆' },
  bottom: { label: 'Bottom', icon: '⬇' },
};

const IPV4_COMPLETE =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;

/** Allow only IPv4-shaped typing: digits and dots, ≤4 octets, each ≤255. */
function sanitizeIpv4Typing(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const parts = cleaned.split('.').slice(0, 4);
  const octets = parts.map((part) => {
    if (part === '') return '';
    const digits = part.replace(/\D/g, '').slice(0, 3);
    if (digits === '') return '';
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n)) return '';
    if (n > 255) return '255';
    return digits;
  });
  return octets.join('.');
}

function normalizeIpv4OnCommit(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  if (IPV4_COMPLETE.test(trimmed)) return trimmed;
  return '';
}

const PORT_KIND_ORDER: Record<string, number> = {
  rj45: 0,
  sfp: 1,
  sfpPlus: 2,
  ac: 3,
  dc: 4,
  pvPlus: 5,
  pvMinus: 6,
  batPlus: 7,
  batMinus: 8,
  usb: 9,
  console: 10,
  hdmi: 11,
  audio: 12,
  coax: 13,
  custom: 14,
};

// Function to get manufacturer badge color
const getManufacturerBadgeColor = (manufacturer: string, isDark: boolean) => {
  const normalized = manufacturer.toLowerCase();
  switch (normalized) {
    case 'ubiquiti':
    case 'ui':
      return isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
    case 'cisco':
      return isDark ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-800';
    case 'netgear':
      return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
    case 'tp-link':
    case 'tplink':
      return isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800';
    case 'd-link':
    case 'dlink':
      return isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800';
    case 'linksys':
      return isDark ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
    case 'aruba':
      return isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-800';
    case 'ruckus':
      return isDark ? 'bg-pink-900/40 text-pink-300' : 'bg-pink-100 text-pink-800';
    case 'meraki':
      return isDark ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-800';
    case 'fortinet':
      return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    default:
      return isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-800';
  }
};

// Function to get equipment icon (matching EquipmentComponent)
const getEquipmentIcon = (equipmentType: string, size: number = 14) => {
  switch (equipmentType) {
    case 'ap':
      return <Wifi size={size} />;
    case 'switch':
      return <Router size={size} />;
    case 'router':
      return <Router size={size} />;
    case 'server':
      return <Server size={size} />;
    case 'camera':
      return <Camera size={size} />;
    case 'cable-run':
      return <Cable size={size} />;
    case 'patch-panel':
      return <Package size={size} />;
    case 'poe-injector':
      return <Zap size={size} />;
    case 'psu':
      return <Zap size={size} />;
    case 'ups':
      return <HardDrive size={size} />;
    case 'wall-outlet':
      return <Plug size={size} />;
    case 'radio-dish':
    case 'sector-antenna':
      return <Radio size={size} />;
    case 'computer':
      return <Monitor size={size} />;
    case 'printer':
      return <Printer size={size} />;
    case 'laptop':
      return <Laptop size={size} />;
    case 'tv':
      return <Tv size={size} />;
    case 'game-console':
      return <Gamepad2 size={size} />;
    case 'media-player':
      return <Music size={size} />;
    case 'generic-node':
      return <Circle size={size} />;
    default:
      return <Package size={size} />;
  }
};

// Function to get equipment color (matching EquipmentComponent)
const getEquipmentColor = (equipmentType: string) => {
  switch (equipmentType) {
    case 'ap':
      return 'bg-yellow-500 border-yellow-600';
    case 'switch':
      return 'bg-blue-500 border-blue-600';
    case 'router':
      return 'bg-red-500 border-red-600';
    case 'server':
      return 'bg-purple-500 border-purple-600';
    case 'camera':
      return 'bg-green-500 border-green-600';
    case 'cable-run':
      return 'bg-gray-500 border-gray-600';
    case 'patch-panel':
      return 'bg-orange-500 border-orange-600';
    case 'poe-injector':
      return 'bg-pink-500 border-pink-600';
    case 'psu':
      return 'bg-lime-500 border-lime-600';
    case 'ups':
      return 'bg-indigo-500 border-indigo-600';
    case 'wall-outlet':
      return 'bg-stone-500 border-stone-600';
    case 'radio-dish':
    case 'sector-antenna':
      return 'bg-sky-500 border-sky-600';
    case 'computer':
      return 'bg-cyan-500 border-cyan-600';
    case 'printer':
      return 'bg-slate-500 border-slate-600';
    case 'laptop':
      return 'bg-teal-500 border-teal-600';
    case 'tv':
      return 'bg-emerald-500 border-emerald-600';
    case 'game-console':
      return 'bg-violet-500 border-violet-600';
    case 'media-player':
      return 'bg-rose-500 border-rose-600';
    case 'generic-node':
      return 'bg-amber-500 border-amber-600';
    default:
      return 'bg-gray-500 border-gray-600';
  }
};

interface FloatingEquipmentPanelProps {
  equipment: PlacedEquipment | null;
  onClose: () => void;
  onPortSelect: (portId: string) => void;
  onOpenEquipment?: (targetEquipment: PlacedEquipment) => void;
  highlightPort?: number | null;
}

const FloatingEquipmentPanel: React.FC<FloatingEquipmentPanelProps> = ({
  equipment,
  onClose,
  onPortSelect,
  onOpenEquipment,
  highlightPort,
}) => {
  const {
    createCableRun,
    isCableRunMode,
    selectedSourceEquipment,
    setSelectedSourceEquipment,
    updateEquipmentConfig,
    panelVisualization,
    colourizePorts,
    canvasZoom,
    isPortVisualizationOpen,
    saveEquipmentConfig,
    getEquipmentConfig,
    badgeAnchor,
    appTheme,
    remapPlacedEquipmentPortNumbers,
    equipmentPanelAdvancedDefault,
    setEquipmentPanelPreviewHidden,
    replaceEquipment,
  } = useProjectStore(
    useShallow((s) => ({
      createCableRun: s.createCableRun,
      isCableRunMode: s.isCableRunMode,
      selectedSourceEquipment: s.selectedSourceEquipment,
      setSelectedSourceEquipment: s.setSelectedSourceEquipment,
      updateEquipmentConfig: s.updateEquipmentConfig,
      panelVisualization: s.panelVisualization,
      colourizePorts: s.colourizePorts,
      canvasZoom: s.canvasZoom,
      isPortVisualizationOpen: s.isPortVisualizationOpen,
      saveEquipmentConfig: s.saveEquipmentConfig,
      getEquipmentConfig: s.getEquipmentConfig,
      badgeAnchor: s.badgeAnchor,
      appTheme: s.appTheme,
      remapPlacedEquipmentPortNumbers: s.remapPlacedEquipmentPortNumbers,
      equipmentPanelAdvancedDefault: s.equipmentPanelAdvancedDefault,
      setEquipmentPanelPreviewHidden: s.setEquipmentPanelPreviewHidden,
      replaceEquipment: s.replaceEquipment,
    }))
  );
  const panelZ = 'z-50';
  const isDark = appTheme === 'dark';
  const drawerBg = isDark ? '#20242b' : '#ffffff';
  const headerBg = isDark ? '#2c3544' : '#e2e8f0';
  const border = isDark ? '#343a43' : '#e5e7eb';
  const text = isDark ? '#e5e7eb' : '#111827';
  const muted = isDark ? '#9ca3af' : '#6b7280';
  const tableText = isDark ? '#f8fafc' : '#000000';
  const tableMuted = isDark ? '#d1d5db' : '#111827';
  const tableGrid = isDark ? '#6b7280' : '#9ca3af';
  const inputBg = isDark ? '#171a1f' : '#f9fafb';
  const stageBg = isDark ? '#171a1f' : '#f3f4f6';
  const chipBg = isDark ? '#262b35' : '#f3f4f6';
  const rowHover = isDark ? '#262b35' : '#f3f4f6';
  const footerBg = isDark ? '#1c2129' : '#f9fafb';
  const inactiveTab = isDark ? '#2b3140' : '#e5e7eb';
  const portLabelInputCls = 'flex-1 px-1.5 py-0.5 rounded text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:italic placeholder-gray-500';
  const liveEquipment = useProjectStore(state => {
    const design = state.getCurrentDesign();
    return design?.equipment.find(eq => eq.id === equipment?.id);
  }) as PlacedEquipment | undefined;
  // Cables are design-scoped (project.cables is a cleared legacy field)
  const cables = useProjectStore(state => state.getCurrentDesign()?.cables || []);
  const designEquipment = useProjectStore(state => state.getCurrentDesign()?.equipment || []);
  const previewHidden = useProjectStore((state) => state.equipmentPanelPreviewHidden);
  
  // Get port panel background color from equipment config (matching EquipmentComponent)
  const portPanelBackground = React.useMemo(() => {
    if (!equipment) return '#e5e7eb';
    const eq = designEquipment.find((e: any) => e.id === equipment.id) || liveEquipment;
    const color = eq?.config?.portPanelBackground || equipment.config?.portPanelBackground || '#e5e7eb';
    return color.startsWith('#') ? color : `#${color}`;
  }, [equipment, designEquipment, liveEquipment]);
  
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHeightMaximized, setIsHeightMaximized] = useState(false);
  const isMinimizedRef = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    isMinimizedRef.current = isMinimized;
  }, [isMinimized]);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [hoverPortId, setHoverPortId] = useState<string | null>(null);
  const [lastHoveredPortId, setLastHoveredPortId] = useState<string | null>(null);
  const [selectedPortId, setSelectedPortId] = useState<string | null>(null);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const labelInputRef = useRef<HTMLInputElement>(null);
  const ipInputRef = useRef<HTMLInputElement>(null);
  const [localAdvanced, setLocalAdvanced] = useState(false);
  const [portTableColWidths, setPortTableColWidths] = useState<Record<PortTableColKey, number>>(loadPortTableColWidths);
  const [resizingPortTableCol, setResizingPortTableCol] = useState<PortTableColKey | null>(null);
  const selectedRowRef = useRef<HTMLTableRowElement | null>(null);
  const isAdvanced = !!equipmentPanelAdvancedDefault || localAdvanced;
  const [localConfig, setLocalConfig] = useState((liveEquipment?.config || equipment?.config) || {});
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'equipment';
    id: string;
    label: string;
  } | null>(null);
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);
  const [isOverrideMenuOpen, setIsOverrideMenuOpen] = useState(false);
  const overrideMenuRef = useRef<HTMLDivElement>(null);
  
  // Multi-face state
  const hasMultiFace = !!(localConfig?.layout?.multiFace || equipment?.config?.layout?.multiFace);
  const [activeFace, setActiveFace] = useState<EquipmentFace>('front');

  // Recent equipment history (last 6)
  const [recentEquipment, setRecentEquipment] = useState<PlacedEquipment[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const previousEquipmentIdRef = useRef<string | null>(null);
  const equipmentClickedRef = useRef(false);
  const lastMaximizeCheckRef = useRef<number>(0);

  // Check if panel should be maximized whenever equipment is set and panel is open
  // This handles the case where user clicks same equipment again after minimizing
  // Use useLayoutEffect to check synchronously before paint
  React.useLayoutEffect(() => {
    if (equipment && isPortVisualizationOpen && isMinimizedRef.current) {
      const now = Date.now();
      // Only maximize if we haven't checked recently (prevents infinite loops)
      if (now - lastMaximizeCheckRef.current > 100) {
        setIsMinimized(false);
        setAnimationKey(prev => prev + 1);
        lastMaximizeCheckRef.current = now;
      }
    }
  }, [equipment?.id, isPortVisualizationOpen, isMinimized]);

  useEffect(() => {
    return subscribeRestoreFloatingEquipmentPanel(() => {
      if (isMinimizedRef.current) {
        setIsMinimized(false);
        setAnimationKey((prev) => prev + 1);
      }
    });
  }, []);

  // Close override menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (overrideMenuRef.current && !overrideMenuRef.current.contains(event.target as Node)) {
        setIsOverrideMenuOpen(false);
      }
    };
    if (isOverrideMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOverrideMenuOpen]);

  useEffect(() => {
    if (equipment) {
      const equipmentId = equipment.id;
      const isNewEquipment = previousEquipmentIdRef.current !== equipmentId;
      
      if (isNewEquipment) {
        setRecentEquipment(prev => {
          // Check if equipment is already in the list
          const existingIndex = prev.findIndex(eq => eq.id === equipmentId);
          
          if (existingIndex !== -1) {
            // Equipment is already in the list, don't reorder - just keep it where it is
            return prev;
          } else {
            // New equipment, add to front
            const filtered = prev.filter(eq => eq.id !== equipmentId);
            return [equipment, ...filtered].slice(0, 6);
          }
        });
        // Trigger animation only when equipment actually changes
        setAnimationKey(prev => prev + 1);
        previousEquipmentIdRef.current = equipmentId;
        equipmentClickedRef.current = true; // Mark that equipment was clicked from canvas
      } else {
        // Same equipment - just update the ref
        previousEquipmentIdRef.current = equipmentId;
        equipmentClickedRef.current = true; // Mark that equipment was clicked from canvas
      }
    } else {
      previousEquipmentIdRef.current = null;
      equipmentClickedRef.current = false;
    }
  }, [equipment?.id, isPortVisualizationOpen]); // Depend on equipment ID and panel open state

  useEffect(() => {
    setIsEditingLabel(false);
  }, [equipment?.id]);

  useEffect(() => {
    if (!equipment) return;
    const next = (liveEquipment?.config || equipment.config) || {};
    setLocalConfig((prev) => {
      // Don't clobber in-progress label typing when the store echoes the update
      if (isEditingLabel) {
        return { ...next, label: prev.label };
      }
      return next;
    });
  }, [liveEquipment?.config, equipment?.config, equipment?.id, isEditingLabel]);

  useEffect(() => {
    if (isEditingLabel && labelInputRef.current) {
      labelInputRef.current.focus();
      labelInputRef.current.select();
    }
  }, [isEditingLabel]);

  // Bump only when layout content actually changes (stable stringify), not on object identity.
  const layoutSignature = JSON.stringify(localConfig?.layout ?? liveEquipment?.config?.layout ?? equipment?.config?.layout ?? null);
  useEffect(() => {
    setLayoutVersion((v) => v + 1);
  }, [layoutSignature]);

  const portCount = equipment ? (liveEquipment?.equipment.specifications?.ports || equipment.equipment.specifications?.ports) || 8 : 8;
  
  const simpleLayout = React.useMemo(() => {
    if (!equipment) return undefined;
    if (hasMultiFace) {
      const multiFaceLayout = localConfig?.layout?.multiFace || liveEquipment?.config?.layout?.multiFace || equipment?.config?.layout?.multiFace;
      if (multiFaceLayout?.faces?.[activeFace]) {
        return multiFaceLayout.faces[activeFace] as SimpleLayout;
      }
      const availableFaces = Object.keys(multiFaceLayout?.faces || {}) as EquipmentFace[];
      if (availableFaces.length > 0 && multiFaceLayout) {
        return multiFaceLayout.faces[availableFaces[0]] as SimpleLayout;
      }
    }
    return (localConfig?.layout?.simple || liveEquipment?.config?.layout?.simple || equipment?.config?.layout?.simple) as SimpleLayout | undefined;
  }, [localConfig?.layout, liveEquipment?.config?.layout, equipment?.config?.layout, layoutVersion, hasMultiFace, activeFace, equipment]);
  
  const layoutToRender: SimpleLayout = simpleLayout || makeTemplateForPorts(portCount);
  
  const availableFaces = React.useMemo(() => {
    if (!equipment || !hasMultiFace) return [];
    const multiFaceLayout = localConfig?.layout?.multiFace || liveEquipment?.config?.layout?.multiFace || equipment?.config?.layout?.multiFace;
    return Object.keys(multiFaceLayout?.faces || {}) as EquipmentFace[];
  }, [hasMultiFace, localConfig?.layout, equipment?.config?.layout, equipment]);

  // Get ports that exist on the current active face
  const portsOnActiveFace = React.useMemo(() => {
    if (!layoutToRender?.ports) return new Set<string>();
    return new Set(layoutToRender.ports.map((p) => layoutPortKey(p)));
  }, [layoutToRender]);

  useEffect(() => {
    setSelectedPortId(null);
    setHoverPortId(null);
    setLastHoveredPortId(null);
  }, [equipment?.id]);

  useEffect(() => {
    setSelectedPortId((prev) => {
      if (prev == null) return prev;
      return portsOnActiveFace.has(prev) ? prev : null;
    });
  }, [activeFace, portsOnActiveFace]);

  const namingScheme = React.useMemo(
    () => getPortNamingScheme(localConfig?.layout?.numberingSchemeId || liveEquipment?.config?.layout?.numberingSchemeId),
    [localConfig?.layout?.numberingSchemeId, liveEquipment?.config?.layout?.numberingSchemeId]
  );

  const sortedFacePorts = React.useMemo(() => {
    const kindRank = (kind: string) => {
      if (kind in PORT_KIND_ORDER) return PORT_KIND_ORDER[kind];
      if (kind.startsWith('custom:')) return PORT_KIND_ORDER.custom;
      return 50;
    };
    return [...(layoutToRender?.ports || [])].sort((a, b) => {
      const kindDiff = kindRank(String(a.kind || '')) - kindRank(String(b.kind || ''));
      if (kindDiff !== 0) return kindDiff;
      const kindNameDiff = String(a.kind || '').localeCompare(String(b.kind || ''));
      if (kindNameDiff !== 0) return kindNameDiff;
      return (a.number || 0) - (b.number || 0);
    });
  }, [layoutToRender]);

  const customLabelsByNumber = React.useMemo(
    () => customLabelsForFace(
      localConfig?.portCustomLabels,
      layoutToRender?.ports || [],
      hasMultiFace ? activeFace : undefined
    ),
    [localConfig?.portCustomLabels, layoutToRender, hasMultiFace, activeFace]
  );

  const portSchemeName = (port: { number: number; kind: string; label?: string }) =>
    port.label || formatPortLabel(namingScheme, port.kind, port.number);

  useEffect(() => {
    if (!isAdvanced || selectedPortId == null) return;
    selectedRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedPortId, isAdvanced, previewHidden]);

  const startPortTableColResize = (key: PortTableColKey, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = portTableColWidths[key];
    const minWidth = MIN_PORT_TABLE_COL_WIDTH[key];
    setResizingPortTableCol(key);
    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (moveEvent: MouseEvent) => {
      const next = Math.max(minWidth, Math.round(startWidth + (moveEvent.clientX - startX)));
      setPortTableColWidths((prev) => {
        if (prev[key] === next) return prev;
        const updated = { ...prev, [key]: next };
        savePortTableColWidths(updated);
        return updated;
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
      setResizingPortTableCol(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  
  // Calculate connected ports - memoized to update when cables change
  const { usedPortIds, portConnections, openCount } = React.useMemo(() => {
    const used: string[] = [];
    const connections: Record<string, { cable: any; connectedEquipment: any }> = {};
    
    if (equipment && cables.length > 0) {
      for (const port of (layoutToRender?.ports || [])) {
        const portKey = layoutPortKey(port);

        const cable = cables.find((c: any) =>
          endpointMatchesLayoutPort(c.from, equipment.id, port, layoutToRender.ports) ||
          endpointMatchesLayoutPort(c.to, equipment.id, port, layoutToRender.ports)
        );
        
        if (cable) {
          const endpoint =
            endpointMatchesLayoutPort(cable.from, equipment.id, port, layoutToRender.ports) ? cable.from : cable.to;
          const parsed = parsePortEndpointId(endpoint);
          const isOldFormat = !parsed?.kind && !parsed?.face;
          const isThisFace = !parsed?.face || parsed.face === activeFace;
          if (isOldFormat && !isThisFace && activeFace !== 'front') {
            continue;
          }
          if (!isOldFormat && parsed?.face && !isThisFace) {
            continue;
          }
          
          used.push(portKey);
          const otherEndId =
            endpointMatchesLayoutPort(cable.from, equipment.id, port, layoutToRender.ports) ? cable.to : cable.from;
          const otherEquipment = designEquipment.find((eq: any) => 
            otherEndId.startsWith(eq.id) || otherEndId.startsWith(eq.equipment.id)
          );
          if (otherEquipment) {
            connections[portKey] = { cable, connectedEquipment: otherEquipment };
          }
        }
      }
    }
    
    const open = portsOnActiveFace.size - used.length;
    return { usedPortIds: used, portConnections: connections, openCount: open };
  }, [equipment, cables, designEquipment, layoutToRender, activeFace, portsOnActiveFace.size]);

  const logicOn = useProjectStore((s) => s.getCurrentDesign()?.logicMode === 'logic');
  const logicPorts = useLogicStore((s) => s.result?.ports);
  const logicPortCaptions = React.useMemo(() => {
    if (!logicOn || !logicPorts || !equipment) return undefined;
    const map: Record<string, string> = {};
    (layoutToRender?.ports || []).forEach((p: any) => {
      const match = Object.values(logicPorts).find(
        (lp) => lp.nodeId === equipment.id && lp.portNumber === p.number && (!lp.kind || lp.kind === String(p.kind))
      );
      if (match?.caption) map[layoutPortKey(p)] = match.caption;
    });
    return Object.keys(map).length ? map : undefined;
  }, [logicOn, logicPorts, layoutToRender, equipment]);

  if (!equipment) return null;

  const handlePortClick = (num: number, kind?: string) => {
    const portId = buildPortEndpointId(equipment.id, num, {
      kind,
      face: hasMultiFace ? activeFace : undefined,
    });
    
    if (isCableRunMode && selectedSourceEquipment) {
      const sourcePortId = selectedSourceEquipment.id;
      
      const sourceOccupied = endpointAtCableCapacity(cables, sourcePortId);
      const destCount = countCablesOnLayoutPort(
        cables,
        equipment.id,
        { number: num, kind },
        layoutToRender?.ports || []
      );
      if (sourceOccupied || destCount >= maxCablesForPortKind(kind)) {
        alert('One or both ports are already at capacity. Please select different ports.');
        return;
      }
      
      createCableRun(sourcePortId, portId, 'ethernet', `Port ${num}`, 'Cat6');
      setSelectedSourceEquipment(null);
      onClose();
    } else if (isCableRunMode) {
      const sourceEquipment = {
        ...equipment,
        id: portId
      };
      setSelectedSourceEquipment(sourceEquipment);
      onClose();
    } else {
      const clicked = (layoutToRender?.ports || []).find((p) =>
        p.number === num && (kind == null || String(p.kind) === String(kind))
      );
      setSelectedPortId(clicked ? layoutPortKey(clicked) : layoutPortKey({ number: num, kind }));
      onPortSelect(portId);
    }
  };

  const handleOpenEquipment = (targetEquipment: PlacedEquipment) => {
    if (onOpenEquipment) {
      onOpenEquipment(targetEquipment);
    }
  };

  const labelFace = hasMultiFace ? activeFace : undefined;

  const persistPortLabel = (portNumber: number, value: string) => {
    const stored = value.trim();
    const nextLabels = setPortCustomLabel(localConfig.portCustomLabels, portNumber, stored, labelFace);
    setLocalConfig((prev) => ({ ...prev, portCustomLabels: nextLabels }));
    updateEquipmentConfig(equipment.id, { portCustomLabels: nextLabels }, { persistToLibrary: false });
  };

  const displayedPortLabel = (portNumber: number) => {
    return getPortCustomLabel(localConfig.portCustomLabels, portNumber, labelFace);
  };

  const detailsPortId = selectedPortId ?? hoverPortId ?? lastHoveredPortId;
  const detailsPortRect = detailsPortId != null
    ? layoutToRender?.ports?.find((p: any) => layoutPortKey(p) === detailsPortId)
    : undefined;
  const detailsCustomLabel = detailsPortRect != null
    ? displayedPortLabel(detailsPortRect.number)
    : '';

  const goToCable = (cable: any) => {
    const {
      setSelectedCable,
      setCablePortEditorOpen,
      setCableConfigOpen,
    } = useProjectStore.getState();
    setCableConfigOpen(false);
    setSelectedCable(cable);
    setCablePortEditorOpen(true);
  };

  const switchToConnectedEquipment = (targetEquipment: PlacedEquipment) => {
    const existingIndex = recentEquipment.findIndex(eq => eq.id === targetEquipment.id);
    if (existingIndex === -1) {
      setRecentEquipment(prev => {
        const filtered = prev.filter(eq => eq.id !== targetEquipment.id);
        return [targetEquipment, ...filtered].slice(0, 6);
      });
    }
    handleOpenEquipment(targetEquipment);
  };

  // Minimized view - compact bar showing recent equipment
  if (isMinimized) {
    return createPortal(
      <div 
        key={`minimized-${animationKey}`}
        className={`fixed bottom-4 left-1/2 -translate-x-1/2 ${panelZ}`}
        data-floating-equipment-panel="true"
        style={{ 
          width: '60%', 
          maxWidth: '500px'
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="rounded-lg shadow-lg overflow-hidden" style={{ backgroundColor: drawerBg, border: `1px solid ${border}` }}>
          <div 
            className="flex items-center gap-2 px-3 py-1.5 cursor-pointer"
            onClick={() => {
              setIsMinimized(false);
              setAnimationKey(prev => prev + 1);
            }}
          >
            {/* Current equipment info */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 border text-white ${getEquipmentColor(equipment.equipment.type)}`}>
                {getEquipmentIcon(equipment.equipment.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate" style={{ color: text }}>
                  {liveEquipment?.config?.label || equipment.equipment.name}
                </div>
                <div className="text-[11px] truncate" style={{ color: muted }}>
                  {equipment.equipment.name}
                </div>
              </div>
            </div>
            
            {/* Recent equipment thumbnails */}
            {recentEquipment.length > 1 && (
              <div className="flex items-center gap-1.5 pl-2" style={{ borderLeft: `1px solid ${border}` }}>
                {recentEquipment.slice(0, 6).map((eq) => {
                  const equipmentIcon = getEquipmentIcon(eq.equipment.type);
                  const equipmentColor = getEquipmentColor(eq.equipment.type);
                  const isActive = eq.id === equipment.id;
                  
                  return (
                    <button
                      key={eq.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEquipment(eq);
                      }}
                      className={`w-7 h-7 rounded flex items-center justify-center transition-all border text-white ${equipmentColor} ${
                        isActive ? 'opacity-100' : 'opacity-60 hover:opacity-100'
                      }`}
                      title={eq.config?.label || eq.equipment.name}
                    >
                      {equipmentIcon}
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex items-center gap-1 pl-2" style={{ borderLeft: `1px solid ${border}` }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMinimized(false);
                  setAnimationKey(prev => prev + 1);
                }}
                className="p-1 rounded transition-colors"
                style={{ color: muted }}
                title="Maximize"
              >
                <ChevronUp size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="p-1 rounded transition-colors"
                style={{ color: muted }}
                title="Close"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    , document.body);
  }

  // Maximized view - full panel
  return createPortal(
      <div 
      key={`maximized-${animationKey}`}
      ref={panelRef}
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 ${panelZ}`}
      data-floating-equipment-panel="true"
      style={{ 
        width: '80%', 
        maxWidth: '1100px'
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Equipment Tabs - Outside grey panel */}
      {recentEquipment.length > 1 && (
        <div className="flex items-end gap-1 px-2 pt-2 pb-0 flex-shrink-0 overflow-x-auto">
          {recentEquipment.slice(0, 6).map((eq) => {
            const equipmentIcon = getEquipmentIcon(eq.equipment.type, 12);
            const equipmentColor = getEquipmentColor(eq.equipment.type);
            const isActive = eq.id === equipment.id;
            
            return (
              <div
                key={eq.id}
                className="group relative"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEquipment(eq);
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-t text-[11px] font-medium whitespace-nowrap"
                  style={{
                    backgroundColor: isActive ? inactiveTab : drawerBg,
                    color: isActive ? text : muted,
                    border: `1px solid ${border}`,
                    borderBottom: isActive ? `1px solid ${inactiveTab}` : `1px solid ${border}`,
                  }}
                  title={eq.config?.label || eq.equipment.name}
                >
                  <div className={`w-3.5 h-3.5 rounded flex items-center justify-center text-white ${equipmentColor}`}>
                    {equipmentIcon}
                  </div>
                  <span className="truncate max-w-[120px]">
                    {eq.config?.label || eq.equipment.name}
                  </span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setRecentEquipment(prev => prev.filter(e => e.id !== eq.id));
                    if (eq.id === equipment.id && recentEquipment.length > 1) {
                      const nextEquipment = recentEquipment.find(e => e.id !== eq.id);
                      if (nextEquipment) {
                        handleOpenEquipment(nextEquipment);
                      } else {
                        onClose();
                      }
                    }
                  }}
                  className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: isDark ? '#3f4654' : '#e5e7eb', color: text }}
                  title="Close tab"
                >
                  <X size={10} />
                </button>
              </div>
            );
          })}
        </div>
      )}
      
      <div 
        className="rounded-lg shadow-lg overflow-hidden flex flex-col"
        style={{
          height: isHeightMaximized ? PANEL_HEIGHT_MAXIMIZED : PANEL_HEIGHT_DEFAULT,
          maxHeight: isHeightMaximized ? PANEL_HEIGHT_MAXIMIZED : PANEL_HEIGHT_DEFAULT,
          transition: 'height 0.2s ease, max-height 0.2s ease',
          backgroundColor: drawerBg,
          border: `1px solid ${border}`,
        }}
      >
        {/* Header */}
        <div
          className="flex-shrink-0 relative"
          style={{ backgroundColor: headerBg, borderBottom: `1px solid ${border}` }}
        >
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 border text-white ${getEquipmentColor(equipment.equipment.type)}`}>
                {getEquipmentIcon(equipment.equipment.type, 14)}
              </div>
              <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                <div className="flex items-center gap-1.5 min-w-0 h-5">
                  {isEditingLabel ? (
                    <input
                      ref={labelInputRef}
                      type="text"
                      value={localConfig.label || ''}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newValue = e.target.value;
                        setLocalConfig((prev) => ({ ...prev, label: newValue }));
                      }}
                      onBlur={(e) => {
                        const value = e.currentTarget.value;
                        setLocalConfig((prev) => ({ ...prev, label: value }));
                        updateEquipmentConfig(equipment.id, { label: value });
                        setIsEditingLabel(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const value = (e.target as HTMLInputElement).value;
                          setLocalConfig((prev) => ({ ...prev, label: value }));
                          updateEquipmentConfig(equipment.id, { label: value });
                          setIsEditingLabel(false);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          const restored = liveEquipment?.config?.label || equipment.config?.label || '';
                          setLocalConfig((prev) => ({ ...prev, label: restored }));
                          setIsEditingLabel(false);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="h-5 w-[120px] max-w-[160px] box-border px-1.5 rounded text-xs font-semibold leading-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                      style={{ backgroundColor: inputBg, border: `1px solid ${border}`, color: text }}
                      placeholder="Label"
                    />
                  ) : localConfig.label ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingLabel(true);
                      }}
                      className="h-5 max-w-[160px] truncate text-xs font-semibold leading-5 hover:underline"
                      style={{ color: text }}
                      title="Edit label"
                    >
                      {localConfig.label}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingLabel(true);
                      }}
                      className="h-5 flex-shrink-0 px-1.5 rounded text-[10px] font-medium leading-none box-border"
                      style={{
                        backgroundColor: isDark ? '#2b3140' : '#e5e7eb',
                        color: muted,
                        border: `1px solid ${border}`,
                      }}
                      title="Add equipment label"
                    >
                      Add Label
                    </button>
                  )}
                  {(isEditingLabel || localConfig.label) && (
                    <span className="flex-shrink-0 leading-5" style={{ color: muted }}>•</span>
                  )}
                  <span className="text-xs font-semibold truncate leading-5" style={{ color: text }}>
                    {equipment.equipment.name}
                  </span>
                  {highlightPort !== null && highlightPort !== undefined && (
                    <span
                      className="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium"
                      style={{
                        backgroundColor: isDark ? 'rgba(234,88,12,0.25)' : '#ffedd5',
                        color: isDark ? '#fdba74' : '#c2410c',
                      }}
                    >
                      Port {highlightPort}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 min-w-0 h-5">
                  <span className={`inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium leading-none ${getManufacturerBadgeColor((liveEquipment?.equipment.manufacturer || equipment.equipment.manufacturer || ''), isDark)}`}>
                    {(liveEquipment?.equipment.manufacturer || equipment.equipment.manufacturer || '').toUpperCase()}
                  </span>
                  <input
                    ref={ipInputRef}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    spellCheck={false}
                    value={localConfig.ip || ''}
                    onChange={(e) => {
                      e.stopPropagation();
                      const newValue = sanitizeIpv4Typing(e.target.value);
                      setLocalConfig((prev) => ({ ...prev, ip: newValue }));
                      updateEquipmentConfig(equipment.id, { ip: newValue });
                    }}
                    onBlur={(e) => {
                      const locked = normalizeIpv4OnCommit(e.currentTarget.value);
                      setLocalConfig((prev) => ({ ...prev, ip: locked }));
                      updateEquipmentConfig(equipment.id, { ip: locked });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const locked = normalizeIpv4OnCommit((e.target as HTMLInputElement).value);
                        setLocalConfig((prev) => ({ ...prev, ip: locked }));
                        updateEquipmentConfig(equipment.id, { ip: locked });
                        ipInputRef.current?.blur();
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="h-5 w-[118px] box-border px-1.5 rounded text-[10px] font-medium leading-none focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:italic placeholder-gray-500"
                    style={{ backgroundColor: inputBg, border: `1px solid ${border}`, color: text }}
                    placeholder="0.0.0.0"
                    title="IPv4 address (e.g. 192.168.0.1)"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-medium mr-0.5" style={{ color: muted }}>Advanced</span>
              <button
                type="button"
                disabled={!!equipmentPanelAdvancedDefault}
                onClick={() => {
                  if (equipmentPanelAdvancedDefault) return;
                  setLocalAdvanced((prev) => !prev);
                }}
                className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                  isAdvanced ? 'bg-blue-600' : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                } ${equipmentPanelAdvancedDefault ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={equipmentPanelAdvancedDefault
                  ? 'Advanced table is on for every panel (Settings)'
                  : (isAdvanced ? 'Show Port Details' : 'Show advanced port table')}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    isAdvanced ? 'translate-x-3.5' : 'translate-x-0.5'
                  }`}
                />
              </button>
              <button
                type="button"
                onClick={() => setEquipmentPanelPreviewHidden(!previewHidden)}
                className="p-1 rounded transition-colors"
                style={{ color: muted }}
                title={previewHidden ? 'Show port preview' : 'Hide port preview'}
              >
                {previewHidden ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              {(() => {
                const live = (useProjectStore.getState() as any).getCurrentDesign()?.equipment?.find((eq: any) => eq.id === equipment.id);
                const isExisting = live ? !!live.isExisting : !!(equipment as any).isExisting;
                return (
                  <>
                  <button 
                    onClick={() => {
                      const { toggleEquipmentExistingStatus } = useProjectStore.getState() as any;
                      toggleEquipmentExistingStatus(equipment.id);
                    }}
                    className="px-2 py-0.5 rounded text-[11px] font-medium"
                    style={{
                      backgroundColor: isExisting
                        ? (isDark ? 'rgba(22,163,74,0.25)' : '#dcfce7')
                        : (isDark ? '#2b3140' : '#e5e7eb'),
                      color: isExisting
                        ? (isDark ? '#4ade80' : '#15803d')
                        : muted,
                    }}
                    title={isExisting ? 'Mark as new infrastructure' : 'Mark as existing infrastructure'}
                  >
                    {isExisting ? 'Existing' : 'New'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNodesLogicExcluded([equipment.id], !isLogicExcluded(equipment.config, equipment.equipment.type))}
                    className="px-2 py-0.5 rounded text-[11px] font-medium"
                    style={{
                      backgroundColor: isLogicExcluded(equipment.config, equipment.equipment.type)
                        ? (isDark ? '#2b3140' : '#e5e7eb')
                        : (isDark ? 'rgba(8,145,178,0.25)' : '#cffafe'),
                      color: isLogicExcluded(equipment.config, equipment.equipment.type) ? muted : (isDark ? '#67e8f9' : '#0e7490'),
                    }}
                    title={isLogicExcluded(equipment.config, equipment.equipment.type) ? 'Add this device to Logic' : 'Exclude this device from Logic'}
                  >
                    {isLogicExcluded(equipment.config, equipment.equipment.type) ? 'Logic off' : 'In Logic'}
                  </button>
                  </>
                );
              })()}
              <button 
                onClick={() => setIsEditorOpen(true)}
                className="p-1 rounded transition-colors"
                style={{ color: muted }} 
                title="Edit layout & electrical"
              >
                <Edit size={14} />
              </button>
              <div className="relative" ref={overrideMenuRef}>
                <button 
                  onClick={() => setIsOverrideMenuOpen(!isOverrideMenuOpen)}
                  className="p-1 rounded transition-colors"
                style={{ color: muted }} 
                  title="Override Display"
                >
                  <Sliders size={14} />
                </button>
                {isOverrideMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 rounded-lg shadow-xl z-50 min-w-[240px]" style={{ backgroundColor: drawerBg, border: `1px solid ${border}` }}>
                    <div className="p-2.5 space-y-2.5" onClick={(e) => e.stopPropagation()}>
                      {/* Badge Position */}
                      <div>
                        <label className="block text-[11px] uppercase tracking-wide mb-1" style={{ color: muted }}>Badge Position</label>
                        <select
                          value={(localConfig as any)?.badgeAnchor || badgeAnchor || 'top-left'}
                          onChange={(e) => {
                            e.stopPropagation();
                            const value = e.target.value;
                            setLocalConfig(prev => ({ ...prev, badgeAnchor: value }));
                            updateEquipmentConfig(equipment.id, { badgeAnchor: value });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full px-2 py-1.5 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          style={{ backgroundColor: inputBg, border: `1px solid ${border}`, color: text, colorScheme: isDark ? 'dark' : 'light' }}
                        >
                          <option value="top-left">Top Left</option>
                          <option value="top-right">Top Right</option>
                          <option value="center-top">Center Top</option>
                          <option value="left">Left</option>
                          <option value="right">Right</option>
                          <option value="bottom-left">Bottom Left</option>
                          <option value="bottom-right">Bottom Right</option>
                          <option value="center-bottom">Center Bottom</option>
                        </select>
                      </div>
                      {/* Badge Expanded */}
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block text-[11px] font-medium" style={{ color: text }}>Always Expanded</label>
                          <p className="text-[10px]" style={{ color: muted }}>Keep badge expanded on canvas</p>
                        </div>
                        <button
                          onClick={() => {
                            const next = !((localConfig as any)?.alwaysExpanded);
                            setLocalConfig(prev => ({ ...prev, alwaysExpanded: next }));
                            updateEquipmentConfig(equipment.id, { alwaysExpanded: next });
                          }}
                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                            (localConfig as any)?.alwaysExpanded ? 'bg-blue-600' : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                          }`}
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              (localConfig as any)?.alwaysExpanded ? 'translate-x-3.5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                      {/* Show Port Panel */}
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block text-[11px] font-medium" style={{ color: text }}>Show Port Panel on Canvas</label>
                          <p className="text-[10px]" style={{ color: muted }}>Display port panel below badge</p>
                        </div>
                        <button
                          onClick={() => {
                            const next = !((localConfig as any)?.showPortPanel !== false);
                            setLocalConfig(prev => ({ ...prev, showPortPanel: next }));
                            updateEquipmentConfig(equipment.id, { showPortPanel: next });
                          }}
                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                            (localConfig as any)?.showPortPanel !== false ? 'bg-blue-600' : (isDark ? 'bg-gray-600' : 'bg-gray-300')
                          }`}
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              (localConfig as any)?.showPortPanel !== false ? 'translate-x-3.5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button 
                onClick={() => {
                  setConfirmDelete({ 
                    type: 'equipment', 
                    id: equipment.id, 
                    label: equipment.equipment.name 
                  });
                }} 
                className="p-1 rounded transition-colors"
                style={{ color: isDark ? '#fca5a5' : '#dc2626' }} 
                title="Delete equipment"
              >
                <Trash2 size={14} />
              </button>
              <button 
                onClick={() => setIsReplaceOpen(true)} 
                className="p-1 rounded transition-colors"
                style={{ color: muted }} 
                title="Replace equipment"
              >
                <ArrowLeftRight size={14} />
              </button>
              <button
                onClick={() => setIsHeightMaximized((prev) => !prev)}
                className="p-1 rounded transition-colors"
                style={{ color: muted }}
                title={isHeightMaximized ? 'Restore height' : 'Maximize'}
              >
                {isHeightMaximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              <button
                onClick={() => {
                  setIsMinimized(true);
                  setAnimationKey(prev => prev + 1);
                }}
                className="p-1 rounded transition-colors"
                style={{ color: muted }}
                title="Minimize"
              >
                <ChevronDown size={14} />
              </button>
            </div>
          </div>
          
          {/* Face Tabs - Centered in header */}
          {hasMultiFace && availableFaces.length > 0 && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-0 flex items-end gap-1" style={{ bottom: '-1px' }}>
              {availableFaces.map((face) => {
                const faceInfo = FACE_INFO[face];
                
                return (
                  <button
                    key={face}
                    onClick={() => setActiveFace(face)}
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded-t text-[11px] font-medium whitespace-nowrap"
                    style={{
                      backgroundColor: activeFace === face ? drawerBg : inactiveTab,
                      color: activeFace === face ? text : muted,
                      border: `1px solid ${border}`,
                      borderBottom: activeFace === face ? `1px solid ${drawerBg}` : `1px solid ${border}`,
                    }}
                    title={faceInfo.label}
                  >
                    <span>{faceInfo.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
          {!previewHidden && (
            <div className="flex-shrink-0 p-2.5" style={{ backgroundColor: stageBg }}>
              <div id="equipment-panel-stage" className="relative overflow-x-auto overflow-y-hidden">
                <div className="flex items-center justify-center w-full min-w-min">
                  <PortStage
                    key={layoutVersion}
                    layout={layoutToRender}
                    activePortIds={usedPortIds}
                    colourizePorts={colourizePorts}
                    matchEditor
                    showChrome={portChromeVisibleAtZoom(canvasZoom)}
                    portCaptions={logicPortCaptions}
                    onPortClick={handlePortClick}
                    onPortHover={(_port, rect) => {
                      const id = rect ? layoutPortKey(rect) : null;
                      setHoverPortId(id);
                      if (id) setLastHoveredPortId(id);
                    }}
                    hoveredPortId={hoverPortId}
                    selectedPortId={selectedPortId}
                    hoverAccent="yellow"
                    portCustomLabels={customLabelsByNumber}
                    style={
                      panelVisualization === 'minimalist'
                        ? { background: 'transparent', border: '1px solid #64748b' }
                        : portPanelBackground && portPanelBackground !== '#e5e7eb'
                          ? { background: portPanelBackground, border: '1px solid #475569' }
                          : { border: '1px solid #475569' }
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* Details, Configuration, or Advanced table */}
          <div className="w-full flex-1 min-h-0 overflow-hidden flex flex-col" style={{ borderTop: `1px solid ${border}` }}>
            {isAdvanced ? (
              <div className="flex-1 min-h-0 overflow-auto">
                <table
                  className="port-advanced-table"
                  style={{
                    width: PORT_TABLE_COLUMNS.reduce((sum, col) => sum + portTableColWidths[col.key], 0),
                    ['--port-table-grid' as string]: tableGrid,
                  }}
                >
                  <colgroup>
                    {PORT_TABLE_COLUMNS.map((col) => (
                      <col key={col.key} style={{ width: portTableColWidths[col.key] }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: footerBg, color: tableText }}>
                      {PORT_TABLE_COLUMNS.map((col) => (
                        <th key={col.key} style={{ backgroundColor: footerBg, width: portTableColWidths[col.key] }}>
                          <span className="port-advanced-col-label">{col.label}</span>
                          <button
                            type="button"
                            aria-label={`Resize ${col.label} column`}
                            className={`port-advanced-col-resizer${resizingPortTableCol === col.key ? ' is-dragging' : ''}`}
                            onMouseDown={(e) => startPortTableColResize(col.key, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ color: tableText }}>
                    {sortedFacePorts.map((port) => {
                      const portKey = layoutPortKey(port);
                      const connected = usedPortIds.includes(portKey);
                      const link = portConnections[portKey];
                      const isRowSelected = selectedPortId === portKey;
                      const isRowHovered = hoverPortId === portKey;
                      const customLabel = displayedPortLabel(port.number);
                      const farEq = link?.connectedEquipment;
                      const farName = farEq
                        ? (farEq.config?.label
                          ? `${farEq.config.label} • ${farEq.equipment.name}`
                          : farEq.equipment.name)
                        : '';
                      return (
                        <tr
                          key={port.id || `${port.kind}-${port.number}`}
                          ref={isRowSelected ? selectedRowRef : undefined}
                          onClick={() => setSelectedPortId(portKey)}
                          style={{
                            backgroundColor: isRowSelected
                              ? (isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe')
                              : isRowHovered
                                ? rowHover
                                : undefined,
                          }}
                          onMouseEnter={() => {
                            setHoverPortId(portKey);
                            setLastHoveredPortId(portKey);
                          }}
                          onMouseLeave={() => setHoverPortId(null)}
                        >
                          <td>{portSchemeName(port)}</td>
                          <td>
                            <input
                              type="text"
                              value={customLabel}
                              placeholder="(none)"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                persistPortLabel(port.number, e.target.value);
                              }}
                              className={portLabelInputCls}
                              style={{ backgroundColor: inputBg, border: `1px solid ${border}`, color: tableText }}
                            />
                          </td>
                          <td style={{ color: tableMuted }}>{portKindDisplayName(port.kind)}</td>
                          <td>
                            {connected ? (
                              <span className={`port-table-badge ${isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-800'}`}>Connected</span>
                            ) : (
                              <span style={{ color: tableMuted }}>Open</span>
                            )}
                          </td>
                          <td>
                            {link ? (
                              <span
                                className={`port-table-badge cursor-pointer ${isDark ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-50 text-blue-800 hover:bg-blue-100'}`}
                                title={`Go To: ${link.cable.label || 'Unnamed Cable'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  goToCable(link.cable);
                                }}
                              >
                                {link.cable.label || 'Unnamed Cable'}
                              </span>
                            ) : (
                              <span style={{ color: tableMuted }}>—</span>
                            )}
                          </td>
                          <td>
                            {farEq ? (
                              <span
                                className={`port-table-badge cursor-pointer ${isDark ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-50 text-blue-800 hover:bg-blue-100'}`}
                                title={`Go To: ${farEq.config?.label || farEq.equipment.name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  switchToConnectedEquipment(farEq);
                                }}
                              >
                                {farName}
                              </span>
                            ) : (
                              <span style={{ color: tableMuted }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
            <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-4">
              {/* Port Details (basic view) */}
              <div className="p-2.5 pb-3 text-xs">
                <p className="text-[11px] uppercase tracking-wide font-medium mb-1.5" style={{ color: muted }}>Port Details</p>
                {detailsPortRect ? (
                  <div className="space-y-1 text-xs" style={{ color: muted }}>
                    <div className="flex items-center gap-2">
                      <span>Port Number:</span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium" style={{ backgroundColor: chipBg, color: text, border: `1px solid ${border}` }}>
                        {portSchemeName(detailsPortRect)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Label:</span>
                      <input
                        type="text"
                        value={detailsCustomLabel}
                        placeholder="(none)"
                        onChange={(e) => {
                          e.stopPropagation();
                          persistPortLabel(detailsPortRect.number, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className={portLabelInputCls}
                        style={{ backgroundColor: inputBg, border: `1px solid ${border}`, color: text }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Type:</span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium" style={{ backgroundColor: chipBg, color: text, border: `1px solid ${border}` }}>
                        {portKindDisplayName(detailsPortRect.kind)}
                      </span>
                    </div>
                    {detailsPortRect.poeOut && (
                      <div className="flex items-center gap-2">
                        <span>PoE mode:</span>
                        <PoeModeSelect
                          equipmentId={equipment.id}
                          port={detailsPortRect}
                          face={hasMultiFace ? activeFace : 'front'}
                          className="flex-1 rounded px-1 py-0.5 text-xs"
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span>Status:</span>
                      {usedPortIds.includes(detailsPortId!) ? (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold ${isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-800'}`}>
                          Connected
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium" style={{ backgroundColor: chipBg, color: muted }}>
                          Open
                        </span>
                      )}
                    </div>
                    {detailsPortId && portConnections[detailsPortId] && (
                      <>
                        <div className="flex items-center gap-2">
                          <span>Cable:</span>
                          <span 
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold cursor-pointer ${isDark ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                            title={`Go To: ${portConnections[detailsPortId].cable.label || 'Unnamed Cable'}`}
                            onClick={() => goToCable(portConnections[detailsPortId].cable)}
                          >
                            {portConnections[detailsPortId].cable.label || 'Unnamed Cable'}
                          </span>
                        </div>
                        <div>Equipment: <span 
                          className={`font-bold cursor-pointer transition-colors ${isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-700 hover:text-blue-800'}`}
                          title={`Go To: ${portConnections[detailsPortId].connectedEquipment.config?.label || portConnections[detailsPortId].connectedEquipment.equipment.name}`}
                          onClick={() => switchToConnectedEquipment(portConnections[detailsPortId].connectedEquipment)}
                        >{
                          portConnections[detailsPortId].connectedEquipment.config?.label 
                            ? `${portConnections[detailsPortId].connectedEquipment.config.label} • ${portConnections[detailsPortId].connectedEquipment.equipment.name}`
                            : portConnections[detailsPortId].connectedEquipment.equipment.name
                        }</span></div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1 text-xs" style={{ color: muted }}>
                    <div>Total ports: {portCount}</div>
                    <div>Connected: {usedPortIds.length}</div>
                    <div>Open: {openCount}</div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Replace Equipment Modal */}
      {isReplaceOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="rounded-lg shadow-2xl w-[520px] max-w-[92vw]" style={{ backgroundColor: drawerBg, border: `1px solid ${border}` }}>
            <div className="px-4 py-2.5 text-sm font-semibold flex items-center justify-between" style={{ borderBottom: `1px solid ${border}`, color: text }}>
              <span>Replace Equipment</span>
              <button className="p-1 rounded" style={{ color: muted }} onClick={() => setIsReplaceOpen(false)}>✕</button>
            </div>
            <div className="p-3 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 gap-1.5">
                {equipmentDatabase.map((eq) => (
                  <button
                    key={eq.id}
                    onClick={() => {
                      (useProjectStore.getState() as any).replaceEquipment(equipment.id, eq);
                      setIsReplaceOpen(false);
                    }}
                    className="text-left px-2.5 py-2 rounded border transition-colors"
                    style={{ borderColor: border, color: text }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = rowHover)}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                  >
                    <div className="font-medium text-xs">{eq.name}</div>
                    <div className="text-[11px]" style={{ color: muted }}>{eq.manufacturer} • {eq.type}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="px-4 py-2.5 flex justify-end gap-2" style={{ borderTop: `1px solid ${border}`, backgroundColor: footerBg }}>
              <button className="px-3 py-1.5 text-xs rounded border" style={{ borderColor: border, color: muted }} onClick={() => setIsReplaceOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Simple Layout Editor - rendered via portal to avoid container constraints */}
      {isEditorOpen && createPortal(
        <SimpleLayoutEditor
          onSave={(layout, equipmentData, saveTarget) => {
            const prevLayout = localConfig?.layout || equipment.config?.layout;
            const remaps = buildPortNumberRemaps(prevLayout, layout);
            const deviceModel = equipmentData?.deviceModel;
            const patch = {
              layout,
              ...(deviceModel ? { deviceModel } : {}),
              portCustomLabels:
                remapPortCustomLabels(localConfig?.portCustomLabels || equipment.config?.portCustomLabels, remaps) ||
                localConfig?.portCustomLabels ||
                equipment.config?.portCustomLabels,
            };
            setLocalConfig((prev) => ({
              ...prev,
              ...patch,
            }));
            setLayoutVersion((v) => v + 1);
            if (saveTarget === 'library' && equipment.equipment.id) {
              const existingConfig = getEquipmentConfig(equipment.equipment.id);
              const mergedConfig = existingConfig
                ? { ...existingConfig, ...patch }
                : { ...patch };
              // Library + every canvas instance (with cable remap / badge detach).
              saveEquipmentConfig(equipment.equipment.id, mergedConfig, { updateExistingInstances: true });
            } else {
              if (remaps.length || prevLayout) {
                remapPlacedEquipmentPortNumbers(equipment.id, remaps, {
                  nextLayout: layout,
                  detachOrphans: true,
                });
              }
              updateEquipmentConfig(equipment.id, patch, { persistToLibrary: false });
            }
            useProjectStore.getState().saveProject();
            setIsEditorOpen(false);
          }}
          onClose={() => setIsEditorOpen(false)}
          equipmentName={equipment.equipment.name}
          equipmentManufacturer={liveEquipment?.equipment.manufacturer || equipment.equipment.manufacturer}
          equipmentLabel={undefined} // Don't show label - editing equipment type, not instance
          initialLayout={
            localConfig?.layout ||
            equipment.config?.layout ||
            getEquipmentConfig(equipment.equipment.id)?.layout ||
            resolveDeviceModel(
              equipment.equipment.id,
              equipment.equipment.type,
              localConfig || equipment.config
            ).model.layout
          }
          portCount={portCount}
          showDualSave
          enableElectrical
          initialDeviceModel={resolveDeviceModel(
            equipment.equipment.id,
            equipment.equipment.type,
            localConfig || equipment.config
          ).model}
          equipmentType={equipment.equipment.type}
          equipmentId={equipment.equipment.id}
        />,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="rounded-lg shadow-2xl w-[360px] max-w-[92vw]" style={{ backgroundColor: drawerBg, border: `1px solid ${border}` }}>
            <div className="px-4 py-2.5 text-sm font-semibold" style={{ borderBottom: `1px solid ${border}`, color: text }}>
              Delete equipment?
            </div>
            <div className="p-4 text-xs" style={{ color: muted }}>
              Are you sure you want to delete <span className="font-medium" style={{ color: text }}>{confirmDelete.label}</span> and its connected cables? This action cannot be undone.
            </div>
            <div className="px-4 py-2.5 flex justify-end gap-2" style={{ borderTop: `1px solid ${border}`, backgroundColor: footerBg }}>
              <button 
                className="px-3 py-1.5 text-xs rounded border" 
                style={{ borderColor: border, color: muted }}
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                onClick={() => {
                  (useProjectStore.getState() as any).removeEquipment(confirmDelete.id);
                  setConfirmDelete(null);
                  onClose();
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  , document.body);
};

export default FloatingEquipmentPanel;

export function FloatingEquipmentPanelHost() {
  const equipment = useProjectStore((s) => s.selectedEquipmentForPortView);
  const setPortVisualizationOpen = useProjectStore((s) => s.setPortVisualizationOpen);
  const setSelectedEquipmentForPortView = useProjectStore((s) => s.setSelectedEquipmentForPortView);
  if (!equipment) return null;
  return (
    <FloatingEquipmentPanel
      equipment={equipment}
      onClose={() => {
        setPortVisualizationOpen(false);
        setSelectedEquipmentForPortView(null);
      }}
      onPortSelect={() => {}}
      onOpenEquipment={(targetEquipment) => {
        setSelectedEquipmentForPortView(targetEquipment);
        setPortVisualizationOpen(true);
      }}
    />
  );
}

